import {ReactNode} from 'react';
import {CareJourneyLog, CareJourneyLogType} from '@/api/dto/care-journey';
import {useActiveRoster} from '@/store/roster_store';
import Avatar from '@common/Avatar';
import AvatarDetails from '@common/AvatarDetails';
import {Content, Link, Title, Wrapper} from '@/components/pages/tasks/task_edit/log/TaskLog.styles';
import {aiAgentUser} from '@/components/pages/tasks/AssignedToTask';
import DisplayHelpers from '@/helpers/display_helpers';
import {useTaskStepName} from "@/hooks/useTaskStepName";

type ActionCallback = (
  careJourneyLog: CareJourneyLog,
  setCurrentTab: (index: number, stepName: string) => void,
  taskStepDisplayValues: Record<string, string>
) => ReactNode;

const stepName = (...[careJourneyLog, setCurrentTab, taskStepDisplayValues]: Parameters<ActionCallback>) => (
  <Link onClick={() => setCurrentTab(0, careJourneyLog.stepName)}>
    {taskStepDisplayValues[careJourneyLog.stepName] ?? DisplayHelpers.humanize(careJourneyLog.stepName)}
  </Link>
);

const taskLogActions: Record<CareJourneyLogType, ActionCallback> = {
  [CareJourneyLogType.ASSIGNED]: (careJourneyLog, setCurrentTab, taskStepDisplayValues) =>
    <>assigned to {stepName(careJourneyLog, setCurrentTab, taskStepDisplayValues)}</>,
  [CareJourneyLogType.UNASSIGNED]: (careJourneyLog, setCurrentTab, taskStepDisplayValues) =>
    <>unassigned {careJourneyLog.stepName ? 'from ' : ''}{
      stepName(careJourneyLog, setCurrentTab, taskStepDisplayValues)}{careJourneyLog.reason ? `. ${careJourneyLog.reason}` : ''}
    </>,
  [CareJourneyLogType.STEP_STARTED]: (careJourneyLog, setCurrentTab, taskStepDisplayValues) =>
    <>started {stepName(careJourneyLog, setCurrentTab, taskStepDisplayValues)}</>,
  [CareJourneyLogType.STEP_COMPLETED]: (careJourneyLog, setCurrentTab, taskStepDisplayValues) =>
    <>completed {stepName(careJourneyLog, setCurrentTab, taskStepDisplayValues)}</>,
  [CareJourneyLogType.STEP_FAILED]: (careJourneyLog, setCurrentTab, taskStepDisplayValues) =>
    <>failed to complete {stepName(careJourneyLog, setCurrentTab, taskStepDisplayValues)}</>,
  [CareJourneyLogType.STEP_SUBMITTED]: (careJourneyLog, setCurrentTab, taskStepDisplayValues) =>
    <>submitted {stepName(careJourneyLog, setCurrentTab, taskStepDisplayValues)}</>,
  [CareJourneyLogType.CLOSED]: () =>
    <>closed task</>,
  [CareJourneyLogType.DUE_DATE_CHANGED]: (careJourneyLog) =>
    <>updated due date to {DisplayHelpers.formatCalendarDate(careJourneyLog.dueDate)}</>,
  [CareJourneyLogType.PRIORITY_CHANGED]: (careJourneyLog) =>
    <>updated priority to {careJourneyLog.priority ? DisplayHelpers.humanize(careJourneyLog.priority) : 'None'}</>,
  [CareJourneyLogType.FILE_DELIVERED]: (careJourneyLog) =>
    <>delivered file{careJourneyLog.reason ? `. ${careJourneyLog.reason}` : ''}</>,
  [CareJourneyLogType.CARE_TEAM_REFERRAL_SENT]: () =>
    <>sent Care Team Referral</>,
  [CareJourneyLogType.CARE_NOTE_MANUALLY_SENT]: () =>
    <>sent Care note</>,
  [CareJourneyLogType.APPOINTMENT_REBOOK_NEEDED]: () =>
    <>marked appointment for rebooking</>,
  [CareJourneyLogType.APPOINTMENT_CREATED]: () =>
    <>created an appointment journey</>,
  [CareJourneyLogType.APPOINTMENT_SEALED]: () =>
    <>marked done scheduling for the appointments</>,
  [CareJourneyLogType.APPOINTMENT_REOPENED]: () =>
    <>reopened scheduling appointments</>,
  [CareJourneyLogType.APPOINTMENT_CANCELLED]: (careJourneyLog) =>
    <>closed this appointment{careJourneyLog.reason ? `. ${careJourneyLog.reason}` : ''}</>,
  [CareJourneyLogType.CAMPAIGN_ENROLLED]: (careJourneyLog) =>
    <>{careJourneyLog.reason || 'enrolled client in outreach campaign'}</>,
  [CareJourneyLogType.CAMPAIGN_MANUALLY_REENROLLED]: (careJourneyLog) =>
    <>{careJourneyLog.reason || 'manually re-enrolled client in outreach campaign'}</>,
  [CareJourneyLogType.CAMPAIGN_ENROLLMENT_FAILED]: (careJourneyLog) =>
    <>could not enroll client in outreach campaign{careJourneyLog.reason ? `. ${careJourneyLog.reason}` : ''}</>,
  [CareJourneyLogType.UNKNOWN]: () => <></>,
};

type Props = {
  careJourneyLog: CareJourneyLog;
  setCurrentTab(index: number, stepName: string): void;
}

export default function TaskLog({ careJourneyLog, setCurrentTab }: Props) {
  const currentRoster = useActiveRoster();
  const {taskStepDisplayValues} = useTaskStepName();

  return (
    <Wrapper>
      <Content>
        <Title>
          {careJourneyLog.user
            ? <AvatarDetails user={careJourneyLog.user} roster={currentRoster} />
            : careJourneyLog.systemOriginated
              ? <Avatar user={aiAgentUser} />
              : <AvatarDetails user={aiAgentUser} roster={currentRoster} />}
          <span>
            {careJourneyLog.user
              ? `${careJourneyLog.user.first_name} ${careJourneyLog.user.last_name}
               ${careJourneyLog.user.deleted ? '(Deleted)' : ''}`
              : careJourneyLog.systemOriginated
                ? 'System'
                : 'Bamboo AI Agent'}
            {' '}
            {taskLogActions[careJourneyLog.logType](careJourneyLog, setCurrentTab, taskStepDisplayValues)}
          </span>
        </Title>
      </Content>
      {DisplayHelpers.formatDateTime(careJourneyLog.eventDatetime)}
    </Wrapper>
  );
}
